import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { serviceAPI, watchAPI } from '../services/api';
import { ArrowLeft, Save, Wrench, Search } from 'lucide-react';

const AddServicePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [watches, setWatches] = useState([]);
  const [watchSearch, setWatchSearch] = useState('');
  const [serviceSource, setServiceSource] = useState('inventory'); // 'inventory' or 'customer'
  const [formData, setFormData] = useState({
    watch_id: '',
    service_type: 'Maintenance',
    status: 'Pending',

    // Watch Info (manual entry if not from inventory)
    watch_brand: '',
    watch_model: '',
    watch_reference: '',
    watch_serial: '',

    // Customer Info
    customer_name: '',
    customer_email: '',
    customer_phone: '',

    // Service Provider
    service_provider: '',
    provider_contact: '',
    provider_phone: '',
    provider_email: '',

    // Dates
    received_date: new Date().toISOString().split('T')[0],
    estimated_completion: '',

    // Costs
    estimated_cost: '',
    customer_charge: '',

    // Details
    service_description: '',
    internal_notes: '',
  });

  useEffect(() => {
    fetchWatches();
  }, []);

  const fetchWatches = async () => {
    try {
      const response = await watchAPI.getAll();
      // API returns {watches: [...]} structure
      const watchesArray = response.data.watches || response.data || [];
      setWatches(Array.isArray(watchesArray) ? watchesArray : []);
    } catch (error) {
      console.error('Error fetching watches:', error);
      setWatches([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleWatchSelect = (e) => {
    const watchId = e.target.value;
    setFormData(prev => ({
      ...prev,
      watch_id: watchId
    }));

    if (watchId) {
      const selectedWatch = watches.find(w => w.id === parseInt(watchId));
      if (selectedWatch) {
        setFormData(prev => ({
          ...prev,
          watch_brand: selectedWatch.brand,
          watch_model: selectedWatch.model,
          watch_reference: selectedWatch.reference || '',
          watch_serial: selectedWatch.serial_number || '',
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        watch_id: formData.watch_id ? parseInt(formData.watch_id) : null,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
        customer_charge: formData.customer_charge ? parseFloat(formData.customer_charge) : null,
        // Convert date strings to ISO datetime format
        received_date: formData.received_date ? new Date(formData.received_date).toISOString() : null,
        estimated_completion: formData.estimated_completion ? new Date(formData.estimated_completion).toISOString() : null,
      };

      // Remove empty strings
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });

      const response = await serviceAPI.create(submitData);
      navigate(`/servicing/${response.data.id}`);
    } catch (err) {
      console.error('Error creating service:', err);

      // Handle validation errors
      let errorMessage = 'Failed to create service record';

      if (err.response?.data?.detail) {
        const detail = err.response?.data?.detail;

        // Check if detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map(error => {
            const field = error.loc ? error.loc.join(' > ') : 'Unknown field';
            return `${field}: ${error.msg}`;
          }).join('\n');
        }
        // Check if detail is a string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // If it's an object, try to extract a message
        else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail, null, 2);
        }
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-800 px-4 py-3 rounded-xl">
          <p className="font-medium whitespace-pre-line">{error}</p>
        </div>
      )}

      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/servicing')}
          className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Wrench className="w-8 h-8 text-slate-900" />
            New Service Record
          </h1>
          <p className="text-gray-600 mt-1">Create a new watch service or repair record</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Service Source Selection */}
        <div className="bg-white rounded-3xl shadow-xl p-6 border-2 border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Service Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => {
                setServiceSource('inventory');
                setFormData(prev => ({ ...prev, watch_id: '', watch_brand: '', watch_model: '', watch_reference: '', watch_serial: '' }));
              }}
              className={`p-6 rounded-2xl border-2 font-semibold transition-all ${
                serviceSource === 'inventory'
                  ? 'bg-gradient-to-br from-slate-900 to-slate-700 text-white border-slate-900 shadow-xl'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-slate-300'
              }`}
            >
              <div className="text-xl mb-2">📦</div>
              <div>Inventory Watch</div>
              <div className="text-xs mt-1 opacity-75">Servicing a watch from your inventory</div>
            </button>

            <button
              type="button"
              onClick={() => {
                setServiceSource('customer');
                setFormData(prev => ({ ...prev, watch_id: '', watch_brand: '', watch_model: '', watch_reference: '', watch_serial: '' }));
              }}
              className={`p-6 rounded-2xl border-2 font-semibold transition-all ${
                serviceSource === 'customer'
                  ? 'bg-gradient-to-br from-amber-600 to-amber-500 text-white border-amber-600 shadow-xl'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300'
              }`}
            >
              <div className="text-xl mb-2">👤</div>
              <div>Customer Watch</div>
              <div className="text-xs mt-1 opacity-75">Servicing a customer's watch</div>
            </button>
          </div>
        </div>

        {/* Watch Information */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Watch Information</h2>

          {serviceSource === 'inventory' ? (
            <div className="mb-6 space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                Select Watch from Inventory <span className="text-red-500">*</span>
              </label>

              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by brand, model, or reference..."
                  value={watchSearch}
                  onChange={(e) => setWatchSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                />
              </div>

              {/* Watch Dropdown */}
              <select
                name="watch_id"
                value={formData.watch_id}
                onChange={handleWatchSelect}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              >
                <option value="">-- Select a watch --</option>
                {Array.isArray(watches) && watches
                  .filter(w => {
                    // Filter by search term
                    if (!watchSearch) return true;
                    const search = watchSearch.toLowerCase();
                    return (
                      w.brand?.toLowerCase().includes(search) ||
                      w.model?.toLowerCase().includes(search) ||
                      w.reference?.toLowerCase().includes(search) ||
                      w.serial_number?.toLowerCase().includes(search)
                    );
                  })
                  .map(watch => (
                    <option key={watch.id} value={watch.id}>
                      {watch.brand} {watch.model} {watch.reference ? `(${watch.reference})` : ''} - {watch.status}
                    </option>
                  ))}
              </select>

              {watchSearch && (
                <p className="text-xs text-gray-500">
                  Showing {watches.filter(w => {
                    const search = watchSearch.toLowerCase();
                    return (
                      w.brand?.toLowerCase().includes(search) ||
                      w.model?.toLowerCase().includes(search) ||
                      w.reference?.toLowerCase().includes(search) ||
                      w.serial_number?.toLowerCase().includes(search)
                    );
                  }).length} of {watches.length} watches
                </p>
              )}
            </div>
          ) : (
            <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
              <p className="text-sm text-amber-900">
                <strong>Customer Watch Service:</strong> Enter the watch details manually below.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Brand <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="watch_brand"
                value={formData.watch_brand}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="e.g., Rolex"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Model <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="watch_model"
                value={formData.watch_model}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="e.g., Submariner"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reference Number
              </label>
              <input
                type="text"
                name="watch_reference"
                value={formData.watch_reference}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="e.g., 116610LN"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Serial Number
              </label>
              <input
                type="text"
                name="watch_serial"
                value={formData.watch_serial}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="Watch serial number"
              />
            </div>
          </div>
        </div>

        {/* Customer Info */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Customer Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Customer Name
              </label>
              <input
                type="text"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="Customer or owner name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="customer_email"
                value={formData.customer_email}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="customer@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone
              </label>
              <input
                type="tel"
                name="customer_phone"
                value={formData.customer_phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>
        </div>

        {/* Service Details */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Service Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Service Type <span className="text-red-500">*</span>
              </label>
              <select
                name="service_type"
                value={formData.service_type}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              >
                <option value="Maintenance">Maintenance</option>
                <option value="Repair">Repair</option>
                <option value="Restoration">Restoration</option>
                <option value="Authentication">Authentication</option>
                <option value="Appraisal">Appraisal</option>
                <option value="Polishing">Polishing</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              >
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Picked Up">Picked Up</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Received Date
              </label>
              <input
                type="date"
                name="received_date"
                value={formData.received_date}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Estimated Completion
              </label>
              <input
                type="date"
                name="estimated_completion"
                value={formData.estimated_completion}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Service Description
              </label>
              <textarea
                name="service_description"
                value={formData.service_description}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none resize-none"
                placeholder="Describe what needs to be serviced or repaired..."
              />
            </div>
          </div>
        </div>

        {/* Service Provider */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Service Provider</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Provider Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="service_provider"
                value={formData.service_provider}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="Watch repair shop or watchmaker"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Contact Person
              </label>
              <input
                type="text"
                name="provider_contact"
                value={formData.provider_contact}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="Contact name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Provider Phone
              </label>
              <input
                type="tel"
                name="provider_phone"
                value={formData.provider_phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Provider Email
              </label>
              <input
                type="email"
                name="provider_email"
                value={formData.provider_email}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="provider@email.com"
              />
            </div>
          </div>
        </div>

        {/* Costs */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Costs & Pricing</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Estimated Cost ($)
              </label>
              <input
                type="number"
                name="estimated_cost"
                value={formData.estimated_cost}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Customer Charge ($)
              </label>
              <input
                type="number"
                name="customer_charge"
                value={formData.customer_charge}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="0.00"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Internal Notes
              </label>
              <textarea
                name="internal_notes"
                value={formData.internal_notes}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none resize-none"
                placeholder="Private notes (not visible to customer)..."
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate('/servicing')}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-2xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Creating...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Create Service Record
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddServicePage;
